import { defineSlotRecipe } from '@chakra-ui/react';
import { tableAnatomy } from '@chakra-ui/react/anatomy';

import {
  dataTableBodyColor,
  dataTableBorderColor,
  dataTableCellPaddingCompactX,
  dataTableCellPaddingX,
  dataTableEditableCellHoverBg,
  dataTableEditableCellOpenBg,
  dataTableEditableCellOpenOutlineColor,
  dataTableHeaderColor,
  dataTableIdentifierColor,
  dataTableInteractiveRowHoverBg,
  dataTableOutlineBorderRadius,
  dataTableRowHeight,
  dataTableSelectedRowBg,
  dataTableSortableHeaderHoverBg,
  dataTableSortedHeaderColor,
  dataTableStripedRowBg,
} from './data-table.kds';

const gridBorder = {
  borderColor: `{colors.${dataTableBorderColor}}`,
  borderRightWidth: '1px',
  borderBottomWidth: '1px',
};

/** Figma grid: interior dividers only — outer edge comes from outline root border. */
const lastColumnNoRightBorder = {
  _last: {
    borderRightWidth: '0',
  },
};

const cellNoWrap = {
  whiteSpace: 'nowrap',
};

const headerBase = {
  ...gridBorder,
  ...lastColumnNoRightBorder,
  ...cellNoWrap,
  h: dataTableRowHeight,
  px: dataTableCellPaddingX,
  textStyle: 'small.medium',
  textTransform: 'uppercase',
  color: dataTableHeaderColor,
  bg: 'neutral.0',
  '&[data-sortable]:hover': {
    bg: dataTableSortableHeaderHoverBg,
  },
  '&[data-sorted]': {
    color: dataTableSortedHeaderColor,
  },
  '&[data-numeric]': {
    textAlign: 'end',
  },
};

const cellBase = {
  ...gridBorder,
  ...lastColumnNoRightBorder,
  ...cellNoWrap,
  h: dataTableRowHeight,
  px: dataTableCellPaddingX,
  textStyle: 'default.regular',
  color: dataTableBodyColor,
  '&[data-identifier]': {
    textStyle: 'default.medium',
    color: dataTableIdentifierColor,
  },
  '&[data-numeric]': {
    textAlign: 'end',
  },
  '&[data-editable]': {
    p: 0,
    _hover: {
      bg: dataTableEditableCellHoverBg,
    },
    '&:has([aria-expanded=true])': {
      bg: dataTableEditableCellOpenBg,
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: dataTableEditableCellOpenOutlineColor,
      outlineOffset: '-2px',
    },
  },
};

const bodyBase = {
  '& tr[data-selected] td': {
    bg: dataTableSelectedRowBg,
  },
};

/** Figma row hover paints each cell (`primary/50` = brand.50) — cell-level so stripes/backgrounds do not block it. */
const interactiveBody = {
  ...bodyBase,
  '& tr:not([data-selected]):hover td': {
    bg: dataTableInteractiveRowHoverBg,
  },
};

/** Bottom grid edge: last tbody row when no footer, else last tfoot row (root-level `:has`). */
const tableBottomEdgeBorders = {
  '&:not(:has(tfoot)) tbody tr:last-child td': {
    borderBottomWidth: '0',
  },
  '&:has(tfoot) tfoot tr:last-child td': {
    borderBottomWidth: '0',
  },
};

const outlineCornerRadius = {
  '& thead tr:first-of-type th:first-of-type': {
    borderTopLeftRadius: dataTableOutlineBorderRadius,
  },
  '& thead tr:first-of-type th:last-of-type': {
    borderTopRightRadius: dataTableOutlineBorderRadius,
  },
  '&:not(:has(tfoot)) tbody tr:last-of-type td:first-of-type': {
    borderBottomLeftRadius: dataTableOutlineBorderRadius,
  },
  '&:not(:has(tfoot)) tbody tr:last-of-type td:last-of-type': {
    borderBottomRightRadius: dataTableOutlineBorderRadius,
  },
  '&:has(tfoot) tfoot tr:last-of-type td:first-of-type': {
    borderBottomLeftRadius: dataTableOutlineBorderRadius,
  },
  '&:has(tfoot) tfoot tr:last-of-type td:last-of-type': {
    borderBottomRightRadius: dataTableOutlineBorderRadius,
  },
};

const rootBase = {
  minWidth: 'full',
  width: 'max-content',
  tableLayout: 'auto',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: `{colors.${dataTableBorderColor}}`,
  borderRadius: dataTableOutlineBorderRadius,
  boxShadow: 'none',
  borderCollapse: 'separate',
  borderSpacing: '0',
  ...tableBottomEdgeBorders,
  ...outlineCornerRadius,
};

/** KDS Data Table slot recipe — Figma spec page 1072:8486 / atom canvas 302:4510. */
export const dataTableSlotRecipe = defineSlotRecipe({
  slots: tableAnatomy.keys(),
  className: 'data-table',
  base: {
    root: rootBase,
    columnHeader: headerBase,
    cell: cellBase,
    row: {
      h: dataTableRowHeight,
    },
    body: bodyBase,
  },
  variants: {
    size: {
      sm: {
        columnHeader: {
          px: dataTableCellPaddingCompactX,
          textStyle: 'small.medium',
        },
        cell: {
          px: dataTableCellPaddingCompactX,
          textStyle: 'small.regular',
        },
      },
      md: {
        columnHeader: headerBase,
        cell: cellBase,
      },
    },
    stickyHeader: {
      true: {
        header: {
          '& :where(tr)': {
            top: 'var(--table-sticky-offset, 0)',
            position: 'sticky',
            zIndex: 1,
          },
        },
      },
    },
    interactive: {
      true: {
        body: interactiveBody,
      },
    },
    striped: {
      true: {
        body: {
          ...bodyBase,
          '& tr:nth-of-type(odd) td': {
            bg: dataTableStripedRowBg,
          },
          // After stripe rule so selected rows keep brand.50 on odd indices.
          '& tr[data-selected] td': {
            bg: dataTableSelectedRowBg,
          },
        },
      },
    },
  },
  defaultVariants: {
    size: 'md',
  },
});
